/**
 * ThemeToggleButton
 *
 * Sun/Moon theme toggle used by the authenticated top bar and the public
 * share page. `useThemeToggle` persists the preference to the backend only
 * when a user is authenticated, so the button is safe on public pages.
 *
 * @module components/ui/ThemeToggleButton
 */
import { memo } from 'react';
import { ActionIcon } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { SunIcon, MoonIcon } from '@/components/ui/Icon';
import { AppTooltip } from '@/components/ui/AppTooltip';
import { useThemeToggle } from '@/hooks';

const THEME_ICON_SIZE = 20;

export const ThemeToggleButton = memo(function ThemeToggleButton() {
  const { t } = useTranslation();
  const { colorScheme, handleThemeToggle } = useThemeToggle();

  return (
    <AppTooltip
      labelKey={
        colorScheme === 'dark'
          ? 'tooltips.theme.switchToLight'
          : 'tooltips.theme.switchToDark'
      }
    >
      <ActionIcon
        variant="subtle"
        size="md"
        aria-label={
          colorScheme === 'dark'
            ? t('map.switchToLightTheme')
            : t('map.switchToDarkTheme')
        }
        onClick={handleThemeToggle}
      >
        {colorScheme === 'dark' ? (
          <SunIcon size={THEME_ICON_SIZE} />
        ) : (
          <MoonIcon size={THEME_ICON_SIZE} />
        )}
      </ActionIcon>
    </AppTooltip>
  );
});
